import { useState, useMemo } from "react";
import { MessageCircle, ArrowRight, X, Sparkles } from "lucide-react";
import { CATEGORIES, PRODUCTS } from "@/lib/products";
import { site } from "@/lib/site";
import { track } from "@/lib/analytics";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const waFor = (productName) =>
  `https://wa.me/${site.whatsappRaw}?text=${encodeURIComponent(
    `Olá! Tenho interesse no produto "${productName}" da Serequímica. Pode me passar mais detalhes e valor?`
  )}`;

export default function Produtos() {
  const [active, setActive] = useState("frigorificos");
  const [selected, setSelected] = useState(null);

  const items = useMemo(
    () => PRODUCTS.filter((p) => p.category === active),
    [active]
  );

  return (
    <section
      id="produtos"
      data-testid="produtos-section"
      className="relative bg-[#f5efe1] py-20 sm:py-28"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <div className="flex items-center gap-2 text-[#8a6b26] uppercase tracking-[0.25em] text-xs font-semibold">
            <Sparkles size={14} /> Produtos em destaque
          </div>
          <h2
            className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0e3b21] leading-tight"
            style={{ fontFamily: "Playfair Display, serif" }}
          >
            Cada rótulo é uma <em className="text-[#8a6b26]">solução técnica</em>{" "}
            desenhada para o seu chão de fábrica.
          </h2>
          <div className="gold-underline mt-5" />
          <p className="mt-6 text-[#1a3a26]/80 text-base sm:text-lg leading-relaxed">
            Selecione o segmento e clique em um produto para abrir a ficha
            completa direto do nosso catálogo técnico.
          </p>
        </div>

        {/* Tabs */}
        <div
          role="tablist"
          className="mt-10 inline-flex bg-white/80 border border-[#0e3b21]/10 rounded-full p-1 shadow-sm flex-wrap"
        >
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              role="tab"
              aria-selected={active === c.id}
              data-testid={`tab-${c.id}`}
              onClick={() => setActive(c.id)}
              className={`px-4 sm:px-6 py-2.5 rounded-full text-sm font-semibold tracking-wide transition-all ${
                active === c.id
                  ? "bg-[#0e3b21] text-white shadow-md"
                  : "text-[#0e3b21]/70 hover:text-[#0e3b21]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Cards */}
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 lg:gap-6">
          {items.map((p) => (
            <article
              key={p.id}
              data-testid={`product-card-${p.id}`}
              className="group bg-white rounded-2xl overflow-hidden shadow-[0_8px_24px_rgba(14,59,33,0.08)] hover:shadow-[0_18px_50px_rgba(14,59,33,0.18)] transition-all duration-500 border border-[#0e3b21]/5 flex flex-col cursor-pointer"
              onClick={() => {
                setSelected(p);
                track("product_view", { product: p.name, id: p.id, category: p.category });
              }}
            >
              <div className="relative aspect-[3/4] overflow-hidden bg-[#0a2a17]">
                <img
                  src={p.thumb}
                  alt={p.name}
                  loading="lazy"
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-widest bg-[#c9a24c] text-[#0e3b21] px-2.5 py-1 rounded-full">
                  {p.tag}
                </div>
              </div>
              <div className="p-5 flex flex-col flex-1">
                <h3
                  className="text-lg font-bold text-[#0e3b21] leading-tight"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  {p.name}
                </h3>
                <p className="mt-2 text-sm text-[#1a3a26]/75 line-clamp-3 flex-1">
                  {p.short}
                </p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-xs font-semibold text-[#8a6b26] uppercase tracking-wider inline-flex items-center gap-1">
                    Ver detalhes <ArrowRight size={12} />
                  </span>
                  <a
                    onClick={(e) => {
                      e.stopPropagation();
                      track("whatsapp_click", { source: "product_card", product: p.name, id: p.id });
                    }}
                    data-testid={`product-wa-${p.id}`}
                    href={waFor(p.name)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-8 w-8 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white flex items-center justify-center transition-all"
                    aria-label={`Falar sobre ${p.name} no WhatsApp`}
                  >
                    <MessageCircle size={14} strokeWidth={2.5} />
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* Product Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent
          data-testid="product-dialog"
          className="p-0 max-w-5xl w-[95vw] bg-transparent border-0 shadow-none [&>button]:hidden"
        >
          {selected && (
            <div className="grid md:grid-cols-5 bg-white rounded-2xl overflow-hidden shadow-2xl max-h-[90vh]">
              <div className="md:col-span-3 bg-[#0a2a17] flex items-center justify-center p-4 relative">
                <img
                  src={selected.full}
                  alt={selected.name}
                  className="max-h-[80vh] w-auto object-contain"
                />
              </div>
              <div className="md:col-span-2 p-6 sm:p-8 flex flex-col overflow-y-auto">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#8a6b26]">
                      {selected.tag}
                    </div>
                    <DialogTitle asChild>
                      <h3
                        className="mt-2 text-2xl sm:text-3xl font-bold text-[#0e3b21]"
                        style={{ fontFamily: "Playfair Display, serif" }}
                      >
                        {selected.name}
                      </h3>
                    </DialogTitle>
                    <DialogDescription className="sr-only">
                      Ficha técnica de {selected.name} — {selected.tag}
                    </DialogDescription>
                  </div>
                  <button
                    onClick={() => setSelected(null)}
                    data-testid="product-dialog-close"
                    className="h-9 w-9 rounded-full border border-[#0e3b21]/10 hover:bg-[#0e3b21] hover:text-white flex items-center justify-center transition-all"
                    aria-label="Fechar"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="gold-underline mt-4" />

                <p className="mt-5 text-[#1a3a26]/85 text-sm sm:text-base leading-relaxed">
                  {selected.short}
                </p>

                <ul className="mt-5 space-y-2">
                  {selected.features.map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-2 text-sm text-[#1a3a26]"
                    >
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#c9a24c] shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>

                <div className="mt-auto pt-8 flex flex-col sm:flex-row gap-3">
                  <a
                    href={waFor(selected.name)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      track("whatsapp_click", {
                        source: "product_dialog",
                        product: selected.name,
                        id: selected.id,
                      })
                    }
                    data-testid="product-dialog-wa"
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold px-5 py-3.5 text-sm transition-all"
                  >
                    <MessageCircle size={16} strokeWidth={2.4} />
                    Quero orçamento no WhatsApp
                  </a>
                </div>

                <div className="mt-3 text-[11px] text-[#0e3b21]/50 text-center">
                  Atendimento consultivo — resposta em minutos.
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
